import { combineEpics, type Epic } from 'redux-observable';
import { filter, mergeMap, of } from 'rxjs';
import i18n from 'i18next';
import { request } from '@/core';
import { openSnackbar } from '@/core/features/snackbar';
import { registerRequest } from '@/api/istaff/auth';
import { register, setRegisterSuccess, setRegisterError } from '../actions';

/*
 * EMAIL VERIFICATION FLOW
 * -----------------------
 * 1. User submits registration form → POST /v1/register
 * 2. Backend sends a verification email containing a link to the FRONTEND:
 *      https://<frontend>/verify-email/{id}/{hash}?expires=...&signature=...
 * 3. User clicks the link → VerifyEmail page loads (src/routes/VerifyEmail)
 * 4. Page auto-POSTs { id, hash, expires, signature } to POST /v1/email/verify
 * 5. On success → user is redirected to login
 *
 * RESEND: POST /v1/email/verify-resend  { email }
 *
 * DEV SHORTCUT
 * ------------
 * In development the register response includes `development_verification_details`
 * with { user_id, hash }. These are stored in Redux (auth.devVerificationDetails) and
 * shown as a "[DEV] Verify email now" button on the registration success screen,
 * so you can verify without a real email. Remove this button before going to prod
 * by checking import.meta.env.DEV (already guarded).
 *
 * TODO: once backend configures the email template to point to the frontend URL,
 * test the full end-to-end flow in staging before releasing.
 */

const registerEpic: Epic = (action$) =>
  action$.pipe(
    filter(register.match),
    request(({ payload }) => registerRequest(payload), register),
  );

const registerSucceededEpic: Epic = (action$) =>
  action$.pipe(
    filter(register.succeeded.match),
    // Pass dev verification details (id + hash) to Redux so the success screen
    // can render the [DEV] shortcut button (only in import.meta.env.DEV).
    mergeMap(({ payload }) =>
      of(setRegisterSuccess(payload?.development_verification_details)),
    ),
  );

const registerFailedEpic: Epic = (action$) =>
  action$.pipe(
    filter(register.failed.match),
    mergeMap(({ payload }) =>
      of(
        setRegisterError(payload),
        openSnackbar({
          snackbarType: 'error',
          snackbarMessage: payload ?? i18n.t('register.error'),
        }),
      ),
    ),
  );

export default combineEpics(
  registerEpic,
  registerSucceededEpic,
  registerFailedEpic,
);
